import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';

import { Driver } from '../domain/driver/Driver';
import { DriverStatus } from '../domain/driver/DriverStatus';
import { Project } from '../domain/project/Project';
import { Stop } from '../domain/stop/Stop';
import { Vehicle } from '../domain/vehicle/Vehicle';
import { driverRepository } from '../repositories/driver';
import { DriverRepository } from '../repositories/driver/DriverRepository';
import { stopRepository } from '../repositories/stop';
import { StopRepository } from '../repositories/stop/StopRepository';
import { vehicleRepository } from '../repositories/vehicle';
import { VehicleRepository } from '../repositories/vehicle/VehicleRepository';
import eventsPanel from '../services/events';
import { INTERCOM_EVENTS, intercom } from '../services/intercom';

export class DriverService {
  constructor(
    private driverRepository: DriverRepository,
    private stopRepository: StopRepository,
    private vehicleRepository: VehicleRepository
  ) {}

  async save({ driver, project }: { driver: Driver; project: Project }): Promise<Driver> {
    const updatedDriver = await this.driverRepository.save({ driver, project });
    return updatedDriver;
  }

  async saveStops({ driver, project }: { driver: Driver; project: Project }): Promise<Driver> {
    const updatedDriver = await this.driverRepository.saveStops({ driver, project });
    return updatedDriver;
  }

  async createDrivers({
    drivers,
    planId,
    project,
  }: {
    drivers: Array<Driver>;
    planId: string;
    project: Project;
  }): Promise<Array<Driver>> {
    const createdDrivers = await this.driverRepository.createDrivers({ drivers, planId, project });
    intercom.track(INTERCOM_EVENTS.plan.route.created, { number: createdDrivers.length });
    return createdDrivers;
  }

  async lockDrivers({ drivers, project }: { drivers: Array<Driver>; project: Project }): Promise<Array<Driver>> {
    const updatedDrivers = await this.driverRepository.lockDrivers({ drivers, project });
    return updatedDrivers;
  }

  async unlockDrivers({ drivers, project }: { drivers: Array<Driver>; project: Project }): Promise<Array<Driver>> {
    const updatedDrivers = await this.driverRepository.unlockDrivers({ drivers, project });
    return updatedDrivers;
  }

  async updateDriversStatus({
    drivers,
    status,
    project,
  }: {
    drivers: Array<Driver>;
    status: DriverStatus;
    project: Project;
  }): Promise<Array<Driver>> {
    const updatedDrivers = await this.driverRepository.updateDriversStatus({ drivers, status, project });
    return updatedDrivers;
  }

  async fetchDriver({ driverId, project }: { driverId: string; project: Project }): Promise<Driver> {
    const driver = await this.driverRepository.fetchDriver({ driverId, project });
    return driver;
  }

  async updateDriverStops({
    driver,
    stops,
    index,
    project,
  }: {
    driver?: Driver;
    stops: Stop[];
    index: number;
    project: Project;
  }): Promise<void> {
    if (!driver) {
      await this.stopRepository.unassign({ stops, project });
    } else {
      // filter stops that are in driver.stops and in stops
      const stopsToAssign = driver.stops.filter((driverStop) => !stops.some((stop) => stop.id === driverStop.id));
      const chunk1 = stopsToAssign.slice(0, index);
      const chunk2 = stopsToAssign.slice(index);
      const newStops = ([] as Stop[]).concat(chunk1, stops, chunk2);

      driver.stops = newStops;
      await this.driverRepository.saveStops({ driver, project });
    }
  }

  async assignStops({ driver, stops, project }: { driver: Driver; stops: Stop[]; project: Project }): Promise<void> {
    driver.stops = stops;
    await this.driverRepository.saveStops({ driver, project });
  }

  async useSaveDriversAsVehicles({
    driverIds,
    project,
  }: {
    driverIds: Array<string>;
    project: Project;
  }): Promise<Array<Vehicle>> {
    const newVehicles = await this.vehicleRepository.createFromDrivers({ driverIds, project });

    return newVehicles;
  }

  async updateConstraints({
    driverIds,
    constraints,
  }: {
    driverIds: string[];
    constraints: Record<string, any>;
  }): Promise<void> {
    return this.driverRepository.updateConstraints(driverIds, constraints);
  }

  async searchDrivers({
    project,
    text,
    offset,
    limit,
    sort,
    status,
  }: {
    project: Project;
    text?: string;
    offset?: number;
    limit?: number;
    sort?: string;
    status?: DriverStatus;
  }): Promise<PaginatedResult<Driver>> {
    return this.driverRepository.searchDrivers({ project, text, offset, limit, sort, status });
  }

  async deleteDrivers({ driverIds, project }: { driverIds: string[]; project: Project }) {
    return this.driverRepository.deleteDrivers({ driverIds, project });
  }

  async optimizeDrivers({ drivers, project }: { drivers: Array<Driver>; project: Project }): Promise<void> {
    await Promise.all(
      drivers.map(async (driver) => {
        const updatedDriver = await this.driverRepository.optimize({ driver, project });

        eventsPanel.routeOptimized(
          updatedDriver.stops.length,
          updatedDriver.stops.reduce(
            (p: any, n: any) => (n.distance_to_next_stop ? p + n.distance_to_next_stop : p),
            updatedDriver.stops.length > 0 ? updatedDriver.stops[0].distance_to_previous_stop ?? 0 : 0
          ),
          (updatedDriver.planned_end_time ?? 0) - (updatedDriver.planned_start_time ?? 0)
        );
      })
    );
  }
}

export const driverService = new DriverService(driverRepository, stopRepository, vehicleRepository);
